import type { Plan, PlanDay, Ridden } from '@painpal/contracts';
import { ChevronRight, Plus } from 'lucide-react';
import { formatDayMonth, formatHoursMinutes, formatWhole, weekdayName } from '../../lib/format';
import { PlanTag } from './PlanTag';
import { formatEstimate, planDuration, rowLabel } from './planModel';
import styles from './calendar.module.css';

function DayCell({ date, today }: { date: string; today: boolean }) {
  return (
    <span className={styles.day}>
      <span className={styles.dayTop}>
        <span className={styles.weekday}>{weekdayName(date)}</span>
        {today && <span className={styles.todayMark}>TODAY</span>}
      </span>
      <span className={styles.date}>{formatDayMonth(date)}</span>
    </span>
  );
}

function PlanCells({ plan, missed }: { plan: Plan; missed: boolean }) {
  const duration = planDuration(plan);
  return (
    <>
      <span className={styles.planCell}>
        <PlanTag type={plan.type} missed={missed} />
        {duration !== null && (
          <span className={styles.duration} data-defaulted={duration.defaulted || undefined}>
            {duration.text}{duration.defaulted && <span className={styles.defaultMark}>DEFAULT</span>}
          </span>
        )}
      </span>
      <span className={styles.note} data-empty={plan.note === null || undefined}>{plan.note ?? '—'}</span>
      <span className={styles.estimate} data-zero={plan.estimatedLoad === 0 || undefined}>
        <span className={styles.figure}>{formatEstimate(plan.estimatedLoad)}</span><span className={styles.unit}>LOAD</span>
      </span>
    </>
  );
}

function RiddenCell({ ridden, status }: { ridden: Ridden; status: PlanDay['status'] }) {
  if (ridden.movingS === 0) return <span className={styles.ridden} data-empty="true">—</span>;
  return (
    <span className={styles.ridden}>
      {status === 'done' && <span className={styles.doneDot} aria-hidden="true" />}
      <span className={styles.riddenWord}>RIDDEN</span>
      <span className={styles.figure}>{formatHoursMinutes(ridden.movingS)}</span> · <span className={styles.figure}>{formatWhole(ridden.load)}</span>
      <span className={styles.unit}>LOAD</span>
    </span>
  );
}

export interface CalendarRowProps { day: PlanDay; today: string; expanded: boolean; onEdit: () => void }

export function CalendarRow({ day, today, expanded, onEdit }: CalendarRowProps) {
  const { plan, ridden, status } = day;
  const isToday = day.date === today;
  return (
    <button
      type="button" className={styles.row} aria-haspopup="dialog" aria-expanded={expanded} aria-label={`Edit ${rowLabel(day)}`} onClick={onEdit}
      data-today={isToday || undefined} data-planned={plan !== null}
    >
      <DayCell date={day.date} today={isToday} />
      {plan === null ? <span className={styles.unplanned}>—</span> : <PlanCells plan={plan} missed={status === 'missed'} />}
      {plan === null && ridden.movingS === 0 ? <span className={styles.addLabel}>ADD BLOCK</span> : <RiddenCell ridden={ridden} status={status} />}
      <span className={styles.edit} aria-hidden="true">{plan === null ? <Plus size={16} /> : <ChevronRight size={16} />}</span>
    </button>
  );
}
